import { MAP_HEIGHT, MAP_WIDTH } from "./constants.js";
import {
  Terrain,
  type MapBase,
  type MapPillbox,
  type MapStart,
  type WorldMap,
} from "./map.js";

// WinBolo .map binary format parser.
//
// Format reference:
//   https://github.com/nbryant42/winbolo/blob/autopilot/winbolo/docs/Map%20File%20Format.txt
//
// Layout:
//   8   bytes  "BMAPBOLO"
//   1   byte   version (MAP_VERSION = 1)
//   1   byte   pillbox count P
//   1   byte   base count B
//   1   byte   start count S
//   P*5 bytes  pillbox records  { x, y, owner, armor, speed }
//   B*6 bytes  base records     { x, y, owner, armor, shells, mines }
//   S*3 bytes  start records    { x, y, dir }
//   ...        terrain runs, ended by the marker { 04 FF FF FF }
//
// Each run:
//   byte 0  datalen (total bytes INCLUDING this 4-byte header)
//   byte 1  y
//   byte 2  sx (first x)
//   byte 3  ex (one past the last x; all tiles outside [sx, ex) are deep sea)
//   then (datalen - 4) bytes of nibble-packed RLE data, high nibble first.
//
// Nibble meaning:
//   0..7   sequence mode: next (n + 1) nibbles are literal tile ids
//   8..15  repeat mode:   next nibble repeats (n - 6) times
//
// Tile id (nibble value) -> terrain:
//   0 Building, 1 River, 2 Swamp, 3 Crater, 4 Road, 5 Forest,
//   6 Rubble, 7 Grass, 8 ShotBuilding, 9 RiverWithBoat,
//   10..15  terrain 2..7 with a neutral mine overlay on that tile.

export class WinboloMapError extends Error {
  constructor(message: string) {
    super(message);
    this.name = "WinboloMapError";
  }
}

export interface ParsedWinboloMap extends WorldMap {
  // Mines authored into the map via the nibble 10..15 overlay. These are
  // surfaced so GameRoom can seed them into World.mines; the core WorldMap
  // shape stays unchanged so the rest of the engine doesn't care where
  // mines came from.
  mines: { x: number; y: number }[];
}

const MAGIC = "BMAPBOLO";

// Tile id nibble -> our Terrain enum value. Indices 10..15 strip the mine
// overlay and map the underlying terrain; the mine is added separately.
const NIBBLE_TO_TERRAIN: readonly Terrain[] = [
  Terrain.Wall,           // 0 Building
  Terrain.River,          // 1 River
  Terrain.Swamp,          // 2 Swamp
  Terrain.Crater,         // 3 Crater
  Terrain.Road,           // 4 Road
  Terrain.Forest,         // 5 Forest
  Terrain.Rubble,         // 6 Rubble
  Terrain.Grass,          // 7 Grass
  Terrain.HalfBuiltWall,  // 8 Shot Building
  Terrain.Boat,           // 9 River with boat
  Terrain.Swamp,          // 10 Swamp + mine
  Terrain.Crater,         // 11 Crater + mine
  Terrain.Road,           // 12 Road + mine
  Terrain.Forest,         // 13 Forest + mine
  Terrain.Rubble,         // 14 Rubble + mine
  Terrain.Grass,          // 15 Grass + mine
];

const NIBBLE_HAS_MINE = [false, false, false, false, false, false, false, false,
                         false, false, true, true, true, true, true, true];

// WinBolo: 0 = East, steps CCW. Ours: 0 = North, steps CW.
// E=0 <-> 4, N=4 <-> 0, W=8 <-> 12, S=12 <-> 8.
export function winboloDirToLocal(wb: number): number {
  return ((4 - wb) % 16 + 16) % 16;
}

export function parseWinboloMap(buf: Uint8Array, displayName: string): ParsedWinboloMap {
  if (buf.length < 12) throw new WinboloMapError("file too short for header");
  for (let i = 0; i < 8; i++) {
    if (buf[i] !== MAGIC.charCodeAt(i)) {
      throw new WinboloMapError("bad magic: expected BMAPBOLO");
    }
  }
  const version = buf[8];
  if (version !== 1) {
    throw new WinboloMapError(`unsupported version ${version}`);
  }
  const nbPills = buf[9];
  const nbBases = buf[10];
  const nbStarts = buf[11];

  let o = 12;
  const needed = nbPills * 5 + nbBases * 6 + nbStarts * 3;
  if (o + needed + 4 > buf.length) {
    throw new WinboloMapError("file truncated before terrain data");
  }

  const pillboxes: MapPillbox[] = [];
  for (let i = 0; i < nbPills; i++) {
    pillboxes.push({
      x: buf[o],
      y: buf[o + 1],
      owner: buf[o + 2] === 0xff ? 0 : buf[o + 2],
      armor: buf[o + 3],
      speed: buf[o + 4],
    });
    o += 5;
  }

  const bases: MapBase[] = [];
  for (let i = 0; i < nbBases; i++) {
    bases.push({
      x: buf[o],
      y: buf[o + 1],
      owner: buf[o + 2] === 0xff ? 0 : buf[o + 2],
      armor: buf[o + 3],
      shells: buf[o + 4],
      mines: buf[o + 5],
    });
    o += 6;
  }

  const starts: MapStart[] = [];
  for (let i = 0; i < nbStarts; i++) {
    starts.push({
      x: buf[o],
      y: buf[o + 1],
      direction: winboloDirToLocal(buf[o + 2]),
    });
    o += 3;
  }

  // Terrain: start as deep sea everywhere, then overwrite with run data.
  const terrain = new Uint8Array(MAP_WIDTH * MAP_HEIGHT);
  terrain.fill(Terrain.DeepSea);
  const mines: { x: number; y: number }[] = [];

  while (o < buf.length) {
    if (o + 4 > buf.length) throw new WinboloMapError("truncated run header");
    const datalen = buf[o];
    const y = buf[o + 1];
    const sx = buf[o + 2];
    const ex = buf[o + 3];
    if (datalen === 4 && y === 0xff && sx === 0xff && ex === 0xff) {
      o += 4;
      break; // end-of-map
    }
    if (datalen < 4) throw new WinboloMapError(`run @${o} datalen=${datalen} < 4`);
    if (o + datalen > buf.length) throw new WinboloMapError(`run @${o} overruns file`);
    if (ex < sx) throw new WinboloMapError(`run @${o} ex=${ex} < sx=${sx}`);
    if (y >= MAP_HEIGHT) throw new WinboloMapError(`run @${o} y=${y} out of bounds`);

    const dataBytes = datalen - 4;
    const nibbles: number[] = new Array(dataBytes * 2);
    for (let i = 0; i < dataBytes; i++) {
      const b = buf[o + 4 + i];
      nibbles[i * 2] = (b >>> 4) & 0xf;
      nibbles[i * 2 + 1] = b & 0xf;
    }

    const expected = ex - sx;
    let written = 0;
    let x = sx;
    let ni = 0;
    while (written < expected) {
      if (ni >= nibbles.length) {
        throw new WinboloMapError(`run @${o} y=${y}: out of nibbles at ${written}/${expected}`);
      }
      const n = nibbles[ni++];
      if (n >= 8) {
        // Repeat mode: next nibble, repeated (n - 6) times.
        if (ni >= nibbles.length) {
          throw new WinboloMapError(`run @${o} y=${y}: repeat needs payload nibble`);
        }
        const tile = nibbles[ni++];
        const reps = n - 6;
        for (let k = 0; k < reps && written < expected; k++, written++, x++) {
          writeTile(terrain, mines, x, y, tile);
        }
      } else {
        // Sequence mode: next (n + 1) nibbles are literal tile ids.
        const count = n + 1;
        for (let k = 0; k < count && written < expected; k++, written++, x++) {
          if (ni >= nibbles.length) {
            throw new WinboloMapError(`run @${o} y=${y}: sequence underrun`);
          }
          writeTile(terrain, mines, x, y, nibbles[ni++]);
        }
      }
    }
    o += datalen;
  }

  // Overlay pillbox and base tiles onto the terrain grid so the renderer
  // paints them correctly. The entities themselves still live in
  // pillboxes/bases lists for AI + refuel logic.
  for (const p of pillboxes) terrain[p.y * MAP_WIDTH + p.x] = Terrain.Pillbox;
  for (const b of bases) terrain[b.y * MAP_WIDTH + b.x] = Terrain.Base;
  // Start tiles are intentionally left as deep sea / river — WinBolo
  // models the tank's arrival as an onBoat state on the tank itself,
  // not a permanent terrain change. See physics.ts for the embark /
  // disembark logic.

  return {
    name: displayName,
    author: "",
    terrain,
    pillboxes,
    bases,
    starts,
    mines,
  };
}

function writeTile(
  terrain: Uint8Array,
  mines: { x: number; y: number }[],
  x: number,
  y: number,
  nibble: number,
): void {
  if (x < 0 || x >= MAP_WIDTH || y < 0 || y >= MAP_HEIGHT) return;
  terrain[y * MAP_WIDTH + x] = NIBBLE_TO_TERRAIN[nibble];
  if (NIBBLE_HAS_MINE[nibble]) mines.push({ x, y });
}
